import * as THREE from 'three';
import { hemi, mesh, rim, rim2, scene, sun } from './scene';
import { edgeRing, floorMesh, gridCoarse, gridFine, terrainGroup } from '../world/arena';
import { P } from '../world/player';
/* ============================================================
   圖鑑掃描艙（3D）：無縫背景 + 三點式棚燈
   ============================================================

   B3 拍板（2026-08-03，`.claude/notes/codex-b3-spec.md`）。舊版是一個「房間」
   —— 展示台圓盤、四根燈桁立柱、底格地板、掃描環、浮塵。實看四種尺寸量出來的
   結論是那個房間在跟展品搶畫面：立柱穿過畫面正中央、掃描環是全畫面最亮的東西、
   台子在下 1/3 而展品在上 1/3。**整個房間拆掉**，換成攝影棚的兩件事：

   1. **無縫背景**：`scene.background` 掛一張垂直漸層（上暗下略亮、沒有水平線）。
      用 `scene.background` 而不是一顆大球 —— 背景貼圖是**全螢幕**畫的，
      不受鏡頭角度與距離影響，所以 0.86m 的 NADEL 與 48m 的 WELTACHSE
      看到的是同一片背景。掛大球的話 600 半徑上的漸層投影到畫面只剩一條線。
   2. **腳下光暈**：展品正下方一片徑向淡出的橢圓。它取代了台子與接地陰影，
      而且**浮空的敷照樣讀得出來**（光暈在地上、展品在空中）。

   ## 三點式棚燈：借現成的三盞，不要新增

   `sun` / `rim` / `rim2` 是戰場那三盞，`enterCodexScene` 進來時整組存起來、
   `exitCodexScene` 原樣還回去。**刻意不新增燈** —— 新增的燈要自己記得移除，
   漏收的症狀是「戰場忽然變亮」而且沒有任何錯誤訊息（handoff 記過）。
   借現成的三盞則是「存了什麼就還什麼」，漏不掉。

     主光 sun   1.00 中性白   鏡頭的右上後方
     補光 rim   0.30 中性白   鏡頭的左下方（只提暗部）
     邊光 rim2  0.55 微冷白   展品後上方（把輪廓從背景拉開）

   **三盞全中性**（使用者拍板）：造型的分面是唯一的細節通道，帶色的燈會把
   深淺判讀弄糊。威脅色改由背景漸層的下緣染一點該隻的 accent 來給。
   機庫那一輪的兩條結論仍然有效：主光要大於所有色光的總和（這裡色光總和是 0），
   ACES 下主光不要超過 ~1.0（高光肩部會往橘黃偏，看起來像暖光其實是曝過頭）。

   ## 燈跟著鏡頭走

   棚燈是**相對鏡頭**佈置的，不是釘在世界座標上。釘在世界座標的話，
   轉到背面就變成逆光剪影 —— 那正是舊版最嚴重的毛病（主光在左前上、
   展品懸在畫面上半，等於逆著光看）。`aimCodexLights()` 每幀依鏡頭位置重算。 */

export var codexGroup = new THREE.Group();
codexGroup.visible = false;
scene.add(codexGroup);

/** 佈局半徑：群體壓力（開發模式）把同型排在這個半徑外圈。
    舊版它同時是展示台圓盤的半徑，台子拆掉之後只剩佈局用途。 */
export var DAIS_R = 2.5;

var built = false;
var glow: any = null, bgTex: any = null, bgCtx: any = null;
var accent = 0x5fe3ff;

/** 背景漸層。上緣接近全黑、下緣略亮，**下緣再染一點該隻的威脅色** ——
    68 張截圖不能長得一模一樣，而顏色語彙是既有的（`TINT`），不是新發明的。
    重畫的成本是一張 4×256 的 canvas，只在換展品時做一次。 */
function paintBg() {
  if (!bgCtx) return;
  var r = (accent >> 16) & 255, g = (accent >> 8) & 255, b = accent & 255;
  var grd = bgCtx.createLinearGradient(0, 0, 0, 256);
  grd.addColorStop(0, '#03050a');
  grd.addColorStop(0.52, '#070b13');
  grd.addColorStop(0.86, 'rgb(' + ((14 + r * 0.055) | 0) + ',' + ((19 + g * 0.055) | 0) + ',' + ((29 + b * 0.055) | 0) + ')');
  grd.addColorStop(1, 'rgb(' + ((19 + r * 0.10) | 0) + ',' + ((25 + g * 0.10) | 0) + ',' + ((37 + b * 0.10) | 0) + ')');
  bgCtx.fillStyle = grd;
  bgCtx.fillRect(0, 0, 4, 256);
  if (bgTex) bgTex.needsUpdate = true;
}

/** 換展品時呼叫。`hex` 給 0 代表未解鎖 —— 那時背景維持中性，
    **威脅色是打死過才給的資訊**（規格的三段解鎖，不要在背景上洩漏）。 */
export function setCodexAccent(hex) {
  accent = hex || 0x2a3a52;
  paintBg();
}

function build() {
  if (built) return;
  built = true;

  /* 腳下光暈：一張徑向淡出的貼圖鋪在 y≈0 的圓面上。
     **不投影、不受光、不寫深度** —— 它是接地的提示，不是一塊地板。 */
  var c = document.createElement('canvas');
  c.width = c.height = 128;
  var g2 = c.getContext('2d');
  var rg = g2.createRadialGradient(64, 64, 0, 64, 64, 64);
  rg.addColorStop(0, 'rgba(150,185,225,0.34)');
  rg.addColorStop(0.45, 'rgba(110,150,200,0.16)');
  rg.addColorStop(1, 'rgba(90,130,180,0)');
  g2.fillStyle = rg; g2.fillRect(0, 0, 128, 128);
  var tex = new THREE.CanvasTexture(c);
  glow = mesh(new THREE.PlaneGeometry(1, 1) as any,
    new THREE.MeshBasicMaterial({
      map: tex, transparent: true, depthWrite: false, opacity: 1,
      blending: THREE.AdditiveBlending
    }), [1, 1, 1], [0, 0.012, 0], [-Math.PI / 2, 0, 0]);
  glow.renderOrder = -1;
  codexGroup.add(glow);
}

/** 光暈的大小跟著展品的腳印走（`place()` 呼叫）。
    0.86m 的 NADEL 與 48m 的 WELTACHSE 差 55 倍，固定大小的光暈在前者是
    一片湖、在後者是一個點。 */
export function setCodexGlow(r) {
  if (!glow) return;
  var s = Math.max(2.2, r * 2.6);
  glow.scale.set(s, s, 1);
}

/** 陰影相機的涵蓋範圍跟著展品走（`place()` 呼叫）。戰場那一組是 ±15 ——
    44m 寬的 SCHLANGE 有一半落在框外，症狀是「半邊沒有自陰影」，
    看起來像那半邊的材質不一樣。離開圖鑑時 `exitCodexScene` 原樣還回去。 */
export function setCodexShadowSpan(halfW, h) {
  var s = Math.max(9, halfW * 1.25);
  var c: any = sun.shadow.camera;
  c.left = -s; c.right = s;
  c.top = Math.max(12, h * 1.15); c.bottom = -s * 0.35;
  c.updateProjectionMatrix();
}

/** 每個畫面幀。`t` 是秒。**唯一會動的東西是光暈的呼吸** ——
    完全靜止的畫面讀起來像截圖，但動得多一點就變回「掃描環搶戲」。 */
export function tickCodexScene(t) {
  if (glow) (glow.material as any).opacity = 0.82 + Math.sin(t * 0.6) * 0.10;
}

/* ---------- 燈跟著鏡頭 ---------- */

var _f = new THREE.Vector3(), _r = new THREE.Vector3(), _u = new THREE.Vector3();
var _t = new THREE.Vector3(), _p = new THREE.Vector3();
var UP = new THREE.Vector3(0, 1, 0);

/** 依鏡頭位置把三盞燈擺到相對位置（`ui/codex.ts` 每幀在擺完鏡頭後呼叫）。
    `camPos` 是鏡頭世界座標，`ty` 是注視高度。 */
export function aimCodexLights(camPos, ty) {
  _t.set(0, ty, 0);
  _f.copy(_t).sub(camPos).normalize();          // 鏡頭 → 展品
  _r.copy(_f).cross(UP).normalize();            // 畫面的右
  _u.copy(_r).clone().cross(_f).normalize();    // 畫面的上
  /* **距離固定 58。** 方向光的距離不影響照明，只影響陰影相機的位置，
     而 `sun.shadow.camera` 的 near/far 是 42–74（`render/scene.ts` 為戰場訂的）。
     跟著取景距離走的話，小怪（鏡頭 4 遠）會落在 near 之前、48m 的 Boss
     會落在 far 之後 —— 兩端都是「陰影靜靜消失」，而自陰影正是分面的主要讀法。 */
  var D = 58;

  _p.copy(_t).addScaledVector(_f, -1.0).addScaledVector(_r, 0.72).addScaledVector(_u, 0.80);
  sun.position.copy(_t).addScaledVector(_p.sub(_t).normalize(), D);
  sun.target.position.copy(_t); sun.target.updateMatrixWorld();

  _p.copy(_t).addScaledVector(_f, -0.85).addScaledVector(_r, -0.95).addScaledVector(_u, -0.10);
  rim.position.copy(_t).addScaledVector(_p.sub(_t).normalize(), D);
  rim.target.position.copy(_t); rim.target.updateMatrixWorld();

  _p.copy(_t).addScaledVector(_f, 1.05).addScaledVector(_r, -0.32).addScaledVector(_u, 0.66);
  rim2.position.copy(_t).addScaledVector(_p.sub(_t).normalize(), D);
  rim2.target.position.copy(_t); rim2.target.updateMatrixWorld();
}

/* ---------- 進出 ---------- */

var saved: any = null;

export function enterCodexScene() {
  build();
  saved = {
    bg: scene.background, fog: scene.fog,
    hemi: hemi.intensity, sunI: sun.intensity, sunC: sun.color.getHex(), sunP: sun.position.clone(),
    rimI: rim.intensity, rimC: rim.color.getHex(), rimP: rim.position.clone(),
    rim2I: rim2.intensity, rim2C: rim2.color.getHex(), rim2P: rim2.position.clone(),
    shadow: { l: (sun.shadow.camera as any).left, r: (sun.shadow.camera as any).right,
      t: (sun.shadow.camera as any).top, b: (sun.shadow.camera as any).bottom },
    floor: floorMesh.visible, terrain: terrainGroup.visible
  };
  floorMesh.visible = false; gridFine.visible = false;
  gridCoarse.visible = false; edgeRing.visible = false; terrainGroup.visible = false;
  /* **玩家的機體要藏起來。** 它站在世界原點，而世界原點正是展品的位置 ——
     不藏的話每一張圖鑑照片的主角都是那台機體，展品被它整隻蓋住。
     （比例尺已經改成畫面左側的刻度標尺，見 `ui/codex-scale.ts`。） */
  if (P.rig && P.rig.root) P.rig.root.visible = false;
  codexGroup.visible = true;

  if (!bgTex) {
    var c = document.createElement('canvas');
    c.width = 4; c.height = 256;
    bgCtx = c.getContext('2d');
    bgTex = new THREE.CanvasTexture(c);
    /* 專案鎖 three 0.128，走的是 `sRGBEncoding`／`outputEncoding` 那一套
       （`platform/renderer.ts` 同一條註解）。升到 r152+ 要一起換成
       `colorSpace = SRGBColorSpace`，否則背景會整片偏亮。 */
    if ((THREE as any).sRGBEncoding) (bgTex as any).encoding = (THREE as any).sRGBEncoding;
    paintBg();
  }
  scene.background = bgTex;
  /* **無縫背景不要霧。** 霧的舊工作是把底格地板的遠緣藏掉，地板拆了之後
     它只剩副作用：44m 的 REQUIEM、48m 的 WELTACHSE 鏡頭要退到 90 以外，
     整隻會落進霧裡變灰（更早的版本是整隻在霧的遠平面外，一片全黑）。
     ⚠️ `codex-model.ts` 的 `place()` 仍然留著「霧跟著取景距離走」那一段 ——
     它有 `isFog` 的守衛，這裡設 null 它就自動不做事。**不要把那段刪掉**，
     哪天有人把霧加回來，那段是唯一擋得住「大 Boss 整隻消失」的東西。 */
  scene.fog = null;

  hemi.intensity = 0.20;
  sun.intensity = 1.00; sun.color.setHex(0xffffff);
  rim.color.setHex(0xffffff); rim.intensity = 0.30;
  rim2.color.setHex(0xdfeaff); rim2.intensity = 0.55;
}

export function exitCodexScene() {
  codexGroup.visible = false;
  if (P.rig && P.rig.root) P.rig.root.visible = true;
  if (!saved) return;
  floorMesh.visible = saved.floor; gridFine.visible = true;
  gridCoarse.visible = true; edgeRing.visible = true; terrainGroup.visible = saved.terrain;
  scene.background = saved.bg; scene.fog = saved.fog;
  hemi.intensity = saved.hemi;
  sun.intensity = saved.sunI; sun.color.setHex(saved.sunC); sun.position.copy(saved.sunP);
  rim.intensity = saved.rimI; rim.color.setHex(saved.rimC); rim.position.copy(saved.rimP);
  rim2.intensity = saved.rim2I; rim2.color.setHex(saved.rim2C); rim2.position.copy(saved.rim2P);
  var sc: any = sun.shadow.camera;
  sc.left = saved.shadow.l; sc.right = saved.shadow.r;
  sc.top = saved.shadow.t; sc.bottom = saved.shadow.b;
  sc.updateProjectionMatrix();
  sun.target.position.set(0, 0, 0); sun.target.updateMatrixWorld();
  rim.target.position.set(0, 0, 0); rim.target.updateMatrixWorld();
  rim2.target.position.set(0, 0, 0); rim2.target.updateMatrixWorld();
  saved = null;
}
